import { useAuth0 } from "@auth0/auth0-react";
import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../lib/api";
import { DEFAULT_LOCALE, getDirection, getHtmlLang } from "../../lib/i18n";

interface LanguageContextType {
	userLanguage: string;
	isLoading: boolean;
}

const LanguageContext = createContext<LanguageContextType>({
	userLanguage: DEFAULT_LOCALE,
	isLoading: true,
});

export function useLanguageContext() {
	return useContext(LanguageContext);
}

export function LanguageProvider({ children }: { children: ReactNode }) {
	const [userLanguage, setUserLanguage] = useState(DEFAULT_LOCALE);
	const [isLoading, setIsLoading] = useState(true);
	const { i18n } = useTranslation();
	const { isAuthenticated, isLoading: auth0Loading } = useAuth0();

	useEffect(() => {
		const safetyTimeout = setTimeout(() => {
			console.warn(
				"LanguageProvider safety timeout triggered, forcing loading to false",
			);
			setIsLoading(false);
		}, 10000);

		return () => clearTimeout(safetyTimeout);
	}, []);

	useEffect(() => {
		const fetchLanguage = async () => {
			// Wait for Auth0 to finish loading before making API calls
			if (auth0Loading) {
				return;
			}

			// Only fetch user language if authenticated
			if (!isAuthenticated) {
				setIsLoading(false);
				return;
			}

			try {
				const controller = new AbortController();
				const timeoutId = setTimeout(() => controller.abort(), 5000);

				const response = await api.get("/api/user/status", {
					signal: controller.signal,
				});

				clearTimeout(timeoutId);

				if (response.data?.display_language) {
					const lang = response.data.display_language;
					setUserLanguage(lang);
					await i18n.changeLanguage(lang);
				}
			} catch (error) {
				if (error instanceof Error && error.name === "AbortError") {
					console.warn(
						"Display language fetch timed out, using default locale",
					);
				} else {
					console.error("Error fetching display language:", error);
				}
			} finally {
				setIsLoading(false);
			}
		};

		fetchLanguage();
	}, [i18n, auth0Loading, isAuthenticated]);

	useEffect(() => {
		const currentLang = i18n.language || DEFAULT_LOCALE;
		const direction = getDirection(currentLang);
		const htmlLang = getHtmlLang(currentLang);

		document.documentElement.setAttribute("dir", direction);
		document.documentElement.setAttribute("lang", htmlLang);
	}, [i18n.language]);

	return (
		<LanguageContext.Provider value={{ userLanguage, isLoading }}>
			{children}
		</LanguageContext.Provider>
	);
}
